import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

/**
 * OptimizedImage Component
 * Lazy loads images with placeholder, blur effect, and error handling
 */
export function OptimizedImage({
  src,
  alt,
  className,
  width,
  height,
  placeholder = '/placeholder-person.svg',
  priority = false,
  onLoad,
  onError,
  ...props
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [inView, setInView] = useState(priority);
  const imgRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || !imgRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '100px', // Start loading 100px before entering viewport
        threshold: 0.01,
      }
    );

    observer.observe(imgRef.current);

    return () => observer.disconnect();
  }, [priority]);

  const handleLoad = (e) => {
    setLoaded(true);
    onLoad?.(e);
  };

  const handleError = (e) => {
    setError(true);
    onError?.(e);
  };

  const imageSrc = error ? placeholder : (inView ? src : placeholder);

  return (
    <div
      ref={imgRef}
      className={cn(
        'relative overflow-hidden bg-slate-100 dark:bg-slate-800',
        className
      )}
      style={{ width, height }}
    >
      {/* Placeholder/blur background */}
      {!loaded && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 dark:from-slate-700 dark:via-slate-800 dark:to-slate-700" />
      )}
      
      {/* Actual image */}
      <img
        src={imageSrc}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
        className={cn(
          'transition-opacity duration-300',
          loaded ? 'opacity-100' : 'opacity-0',
          'w-full h-full object-cover'
        )}
        {...props}
      />
    </div>
  );
}

/**
 * Avatar Component with optimized loading
 */
export function Avatar({
  src,
  alt,
  name,
  size = 'md',
  className,
  ...props
}) {
  const [error, setError] = useState(false);
  
  const sizes = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-lg',
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  if (error || !src) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-full bg-safety-orange-100 dark:bg-safety-orange-900/30 text-safety-orange-600 dark:text-safety-orange-400 font-semibold',
          sizes[size],
          className
        )}
        role="img"
        aria-label={alt || name || 'Avatar'}
        {...props}
      >
        {getInitials(name)}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt || name || 'Avatar'}
      onError={() => setError(true)}
      loading="lazy"
      decoding="async"
      className={cn(
        'rounded-full object-cover',
        sizes[size],
        className
      )}
      {...props}
    />
  );
}

/**
 * Skeleton component for loading states
 */
export function Skeleton({ className, ...props }) {
  return (
    <div
      className={cn(
        'animate-pulse rounded-md bg-slate-200 dark:bg-slate-700',
        className
      )}
      {...props}
    />
  );
}

/**
 * ImageWithFallback - Simple image with fallback
 */
export function ImageWithFallback({
  src,
  fallback,
  alt,
  className,
  ...props
}) {
  const [imgSrc, setImgSrc] = useState(src);

  useEffect(() => {
    setImgSrc(src);
  }, [src]);

  return (
    <img
      src={imgSrc}
      alt={alt}
      onError={() => setImgSrc(fallback)}
      loading="lazy"
      decoding="async"
      className={className}
      {...props}
    />
  );
}

export default OptimizedImage;
